// =============================================================================
// MeuDriver — M2: Hook de Aprovação de Instrutores (Admin)
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface InstructorApplication {
  id: string
  profile_id: string
  bio: string | null
  years_experience: number | null
  cnh_number: string | null
  cnh_verified: boolean
  vehicle_model: string | null
  vehicle_year: number | null
  vehicle_plate: string | null
  hourly_rate: number | null
  status: string
  rejection_reason: string | null
  reviewed_at: string | null
  reviewed_by: string | null
  created_at: string
  profile: {
    full_name: string | null
    avatar_url: string | null
    city: string | null
    state: string | null
    phone: string | null
  } | null
}

// ---------------------------------------------------------------------------
// usePendingInstructors
// ---------------------------------------------------------------------------

export function usePendingInstructors() {
  return useQuery<InstructorApplication[]>({
    queryKey: ['admin-pending-instructors'],
    staleTime: 1000 * 30,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('instructors')
        .select(`
          id, profile_id, bio, years_experience, cnh_number, cnh_verified,
          vehicle_model, vehicle_year, vehicle_plate, hourly_rate, status,
          rejection_reason, reviewed_at, reviewed_by, created_at,
          profile:profiles!instructors_profile_id_fkey(
            full_name, avatar_url, city, state, phone
          )
        `)
        .eq('status', 'pending')
        .order('created_at', { ascending: true })

      if (error) throw error
      return (data ?? []) as InstructorApplication[]
    },
  })
}

// ---------------------------------------------------------------------------
// useInstructorApplications — todas as candidaturas com filtro de status
// ---------------------------------------------------------------------------

export function useInstructorApplications(status: 'pending' | 'approved' | 'rejected' | 'suspended') {
  return useQuery<InstructorApplication[]>({
    queryKey: ['admin-instructor-applications', status],
    staleTime: 1000 * 30,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('instructors')
        .select(`
          id, profile_id, bio, years_experience, cnh_number, cnh_verified,
          vehicle_model, vehicle_year, vehicle_plate, hourly_rate, status,
          rejection_reason, reviewed_at, reviewed_by, created_at,
          profile:profiles!instructors_profile_id_fkey(
            full_name, avatar_url, city, state, phone
          )
        `)
        .eq('status', status)
        .order('created_at', { ascending: false })

      if (error) throw error
      return (data ?? []) as InstructorApplication[]
    },
  })
}

// ---------------------------------------------------------------------------
// useInstructorApplication — candidatura individual
// ---------------------------------------------------------------------------

export function useInstructorApplication(id: string | null) {
  return useQuery<InstructorApplication | null>({
    queryKey: ['admin-instructor-application', id],
    enabled: !!id,
    staleTime: 1000 * 60,
    queryFn: async () => {
      if (!id) return null
      const { data, error } = await supabase
        .from('instructors')
        .select(`
          id, profile_id, bio, years_experience, cnh_number, cnh_verified,
          vehicle_model, vehicle_year, vehicle_plate, hourly_rate, status,
          rejection_reason, reviewed_at, reviewed_by, created_at,
          profile:profiles!instructors_profile_id_fkey(
            full_name, avatar_url, city, state, phone
          )
        `)
        .eq('id', id)
        .single()

      if (error) throw error
      return (data ?? null) as InstructorApplication | null
    },
  })
}

// ---------------------------------------------------------------------------
// useApproveInstructor
// ---------------------------------------------------------------------------

export function useApproveInstructor() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (instructorId: string) => {
      const { error } = await supabase.rpc('admin_approve_instructor', {
        p_instructor_id: instructorId,
      })
      if (error) throw error
    },
    onSuccess: () => {
      toast.success('Instrutor aprovado com sucesso!')
      queryClient.invalidateQueries({ queryKey: ['admin-pending-instructors'] })
      queryClient.invalidateQueries({ queryKey: ['admin-instructor-applications'] })
    },
    onError: (err: Error) => {
      toast.error(`Erro ao aprovar: ${err.message}`)
    },
  })
}

// ---------------------------------------------------------------------------
// useRejectInstructor
// ---------------------------------------------------------------------------

export function useRejectInstructor() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ instructorId, reason }: { instructorId: string; reason: string }) => {
      const { error } = await supabase.rpc('admin_reject_instructor', {
        p_instructor_id: instructorId,
        p_reason: reason,
      })
      if (error) throw error
    },
    onSuccess: () => {
      toast.success('Instrutor rejeitado.')
      queryClient.invalidateQueries({ queryKey: ['admin-pending-instructors'] })
      queryClient.invalidateQueries({ queryKey: ['admin-instructor-applications'] })
    },
    onError: (err: Error) => {
      toast.error(`Erro ao rejeitar: ${err.message}`)
    },
  })
}
